import React, { useMemo, useState } from "react";
import { Layers, Plus } from "lucide-react";
import type { Resource, VmsRole } from "../../../features/rbac/types";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { EmptyState } from "../shared/EmptyState";
import { FormModal, LabeledField, SelectInput, TextInput } from "../shared/FormModal";
import { RowActions } from "../shared/RowActions";
import { SearchInput } from "../shared/SearchInput";
import { SectionCard } from "../shared/SectionCard";
import { useRbacAction } from "../hooks/useRbacAction";

interface ResourcesTabProps {
  resources: Resource[];
  vmsRoles: VmsRole[];
  onCreate: (payload: { resourceName: string; parentRole: number }) => void;
  onUpdate: (payload: { resourceId: number; resourceName?: string; parentRole?: number }) => void;
  onDelete: (resourceId: number) => void;
}

interface ResourceFormState {
  resourceName: string;
  parentRole: string;
}

const emptyForm: ResourceFormState = { resourceName: "", parentRole: "" };

export const ResourcesTab: React.FC<ResourcesTabProps> = ({
  resources,
  vmsRoles,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { matches, isBusy } = useRbacAction();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Resource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);
  const [form, setForm] = useState<ResourceFormState>(emptyForm);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resources;
    return resources.filter(
      (r) =>
        r.resourceName.toLowerCase().includes(q) ||
        String(r.resourceId).includes(q) ||
        String(r.parentRole).includes(q)
    );
  }, [resources, search]);

  const roleNameById = useMemo(
    () => new Map(vmsRoles.map((r) => [r.roleId, r.role])),
    [vmsRoles]
  );

  const openCreate = () => {
    setForm({ resourceName: "", parentRole: vmsRoles[0] ? String(vmsRoles[0].roleId) : "" });
    setCreateOpen(true);
  };

  const openEdit = (resource: Resource) => {
    setForm({
      resourceName: resource.resourceName,
      parentRole: String(resource.parentRole ?? ""),
    });
    setEditTarget(resource);
  };

  const closeForm = () => {
    setCreateOpen(false);
    setEditTarget(null);
    setForm(emptyForm);
  };

  const trimmedName = form.resourceName.trim();
  const parsedParent = Number(form.parentRole);
  const submitDisabled = !trimmedName || !form.parentRole || Number.isNaN(parsedParent);

  const handleSubmit = () => {
    if (submitDisabled) return;
    if (editTarget) {
      onUpdate({
        resourceId: editTarget.resourceId,
        resourceName: trimmedName !== editTarget.resourceName ? trimmedName : undefined,
        parentRole: parsedParent !== editTarget.parentRole ? parsedParent : undefined,
      });
    } else {
      onCreate({ resourceName: trimmedName, parentRole: parsedParent });
    }
    closeForm();
  };

  return (
    <SectionCard
      title="Resources"
      description="Domain entities (e.g. program, distributor, voucher) that permissions are granted on."
      actions={
        <>
          <SearchInput value={search} onChange={setSearch} placeholder="Search resources" />
          <button
            type="button"
            onClick={openCreate}
            disabled={isBusy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            New resource
          </button>
        </>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Layers className="h-5 w-5" />}
          title={search ? "No resources match your search" : "No resources yet"}
          description={search ? "Try a different search term." : "Define your first resource."}
          action={
            !search && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90"
              >
                <Plus className="h-4 w-4" />
                New resource
              </button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Resource</th>
                <th className="px-4 py-2">Parent role</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((resource) => (
                <tr
                  key={resource.resourceId}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {resource.resourceId}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{resource.resourceName}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {resource.parentRole} ({roleNameById.get(resource.parentRole) ?? "unknown"})
                  </td>
                  <td className="px-4 py-3">
                    <RowActions
                      onEdit={() => openEdit(resource)}
                      onDelete={() => setDeleteTarget(resource)}
                      editing={matches("updateResource", resource.resourceId)}
                      deleting={matches("deleteResource", resource.resourceId)}
                      disabled={isBusy}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={createOpen || editTarget != null}
        title={editTarget ? `Edit resource · ${editTarget.resourceName}` : "Create new resource"}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={
          editTarget
            ? matches("updateResource", editTarget.resourceId)
            : matches("createResource")
        }
        submitDisabled={submitDisabled}
        submitLabel={editTarget ? "Save changes" : "Create resource"}
      >
        <LabeledField label="Resource name" required>
          <TextInput
            value={form.resourceName}
            onChange={(e) => setForm((f) => ({ ...f, resourceName: e.target.value }))}
            placeholder="e.g. program"
            autoFocus
          />
        </LabeledField>
        <LabeledField label="Parent role" required hint="The owning VMS role for this resource.">
          {vmsRoles.length > 0 ? (
            <SelectInput
              value={form.parentRole}
              onChange={(e) => setForm((f) => ({ ...f, parentRole: e.target.value }))}
            >
              <option value="" disabled>
                Select a role
              </option>
              {vmsRoles.map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.roleId} · {r.role}
                </option>
              ))}
            </SelectInput>
          ) : (
            <TextInput
              value={form.parentRole}
              onChange={(e) =>
                setForm((f) => ({ ...f, parentRole: e.target.value.replace(/[^0-9]/g, "") }))
              }
              placeholder="VMS role ID"
              inputMode="numeric"
            />
          )}
        </LabeledField>
      </FormModal>

      <ConfirmDialog
        open={deleteTarget != null}
        title={`Delete resource "${deleteTarget?.resourceName ?? ""}"?`}
        description="Mappings and role-resources that reference this resource may break."
        confirmLabel="Delete resource"
        destructive
        loading={deleteTarget ? matches("deleteResource", deleteTarget.resourceId) : false}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.resourceId);
            setDeleteTarget(null);
          }
        }}
      />
    </SectionCard>
  );
};
